import { useState } from 'react'
import axios from 'axios'
import { REDIRECT_LOGIN_URL } from 'helpers/constants'
import { showErrorToast } from 'ui/components/Toast/toast'
import { useOktaAuth } from '@okta/okta-react'

// Backend services sometimes wrap errors as "Some prefix: {\"message\":\"actual reason\", ...}";
// unwrap to the innermost human-readable message so the UI doesn't toast raw nested JSON.
const extractInnermostMessage = (message) => {
  if (typeof message !== 'string') return message
  let current = message
  for (let depth = 0; depth < 5; depth += 1) {
    const jsonStart = current.indexOf('{')
    if (jsonStart === -1) break
    try {
      const parsed = JSON.parse(current.slice(jsonStart))
      // Guard against empty/blank inner messages so a valid outer message is never replaced with nothing.
      if (parsed && typeof parsed.message === 'string' && parsed.message.trim().length > 0) {
        current = parsed.message
      } else {
        break
      }
    } catch {
      break
    }
  }
  return current
}

const useApiRequest = () => {
  const [data, setData] = useState(null)
  const [status, setStatus] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const { oktaAuth } = useOktaAuth()

  const makeRequest = async (
    url,
    method,
    body = null,
    callback = null,
    headers = {},
    responseType = 'json',
    returnFullResponse = false
  ) => {
    setIsLoading(true)
    setError(null)

    try {
      const options = {
        method,
        url,
        responseType,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
          ...headers,
        },
        data: body,
      }

      const response = await axios(options)
      setStatus(response?.status)

      if (response?.data !== undefined && response?.data !== null && response?.data !== '') {
        setData(response.data) // Only update if there's actual data
      }

      if (typeof callback === 'function') {
        callback(response)
      }
      return returnFullResponse ? response : response?.data
    } catch (err) {
      let errorMessage = 'Something went wrong'
      if (err?.code === 'ERR_NETWORK') {
        showErrorToast('Network error occurred. Please check your connection or the server URL.')
      }
      if (err?.response?.status === 401) {
        errorMessage = 'Session expired. Please login again.'
        showErrorToast(errorMessage)
        setIsLoading(false)
        // sessionStorage.removeItem('auth_token')
        // sessionStorage.removeItem('isUserFromAgHub')
        // sessionStorage.removeItem('app_user_details')
        // window.location.href = `${REDIRECT_LOGIN_URL}`
        const tokens = await oktaAuth.tokenManager.getTokens()
        const idToken = tokens?.idToken
        await oktaAuth.signOut({
          // Ensure RP-initiated logout ends the Okta session
          idToken,
          // Ask SDK to revoke tokens for you (don’t revoke manually beforehand)
          revokeAccessToken: true,
          revokeRefreshToken: true,
          // Send the user to a page that doesn't auto-trigger login
          postLogoutRedirectUri: `${window.location.origin}`,
        })
      }

      if (err?.response) {
        // Check different possible locations for the error message
        if (err?.response?.data) {
          if (typeof err.response.data === 'string') {
            // If the response is a string, use it directly
            errorMessage = err.response.data
          } else if (Array.isArray(err?.response?.data) && err?.response?.data?.[0]?.message) {
            // Validation API can return an array of error objects
            errorMessage = err.response.data[0].message
          } else if (err?.response?.data?.message) {
            // Standard error object with message property
            errorMessage = err.response.data.message
          } else if (err?.response?.data?.error) {
            // Some APIs use an error property
            errorMessage = err.response.data.error
          } else if (err?.response?.data?.detail) {
            // Some APIs use a detail property
            errorMessage = err.response.data.detail
          }
        }
      } else if (err?.message) {
        // Network errors might only have a message property
        errorMessage = err.message
      }
      // TODO: In the future, when our backend is ready, we should show a more specific error message
      // showErrorToast(errorMessage)

      errorMessage = extractInnermostMessage(errorMessage)

      setError(errorMessage)
      return {
        status: err?.status,
        message: errorMessage || err?.response?.data?.message,
        errorCode: err?.response?.data?.errorCode,
        details: err?.response?.data?.details,
      }
    } finally {
      setIsLoading(false)
    }
  }

  const getData = (url, callback = null, headers = {}, responseType = 'json') =>
    makeRequest(url, 'GET', null, callback, headers, responseType)

  const getDataWithResponse = (url, callback = null, headers = {}, responseType = 'json') =>
    makeRequest(url, 'GET', null, callback, headers, responseType, true)

  const postData = (
    url,
    body,
    callback,
    headers,
    responseType = 'json',
    returnFullResponse = false
  ) => makeRequest(url, 'POST', body, callback, headers, responseType, returnFullResponse)

  const putData = (url, body, callback) => makeRequest(url, 'PUT', body, callback)
  const patchData = (url, body, callback) => makeRequest(url, 'PATCH', body, callback)
  const deleteData = (url, callback, body = null) => makeRequest(url, 'DELETE', body, callback)

  return {
    data,
    status,
    isLoading,
    error,
    getData,
    getDataWithResponse,
    postData,
    putData,
    patchData,
    deleteData,
  }
}

export default useApiRequest
